/**
 * useCascadingOptions
 * --------------------------------------------------------------------------
 * Single source of truth for "fetch options for a risk-calculator cascading
 * select dropdown". Wraps `useInfiniteQuery` so the consumer (UnifiedCascadingSelect)
 * gets the same shape regardless of mode:
 *
 *   - RESTRICTED (showAll=false): hits `RiskCalculatorService.getCascadingOptions`
 *     — returns all valid options for the current cascade in one shot, no pagination.
 *   - SHOW-ALL (showAll=true): hits `RiskCalculatorService.getAllOptions` —
 *     paginated, backend-searched list ignoring the cascade narrowing.
 *
 * Both modes share the SAME RQ key shape; flipping `showAll` produces a
 * different key → RQ swaps caches cleanly. The component never branches on
 * mode for data; it just reads `data.pages.flatMap(p => p.options)`.
 *
 * Why this hook exists
 * --------------------------------------------------------------------------
 * The previous file (and its companion in UnifiedCascadingSelect.jsx) ran TWO
 * parallel state machines: a clean RQ-backed restricted mode AND a hand-rolled
 * show-all mode with manual `useState` for options/loading/pagination, a
 * lodash/debounce closure, sequence numbers, race-condition refs, and a
 * "phantom empty onSearch" suppression flag. The flag occasionally missed an
 * AntD-emitted empty event, the controlled `searchValue` got wiped, and the
 * user saw their typed text disappear while the filtered list stayed on screen.
 *
 * One unified RQ-backed flow kills the entire class of bugs:
 *   - `placeholderData: keepPreviousData` removes the flicker between keystrokes.
 *   - `AbortSignal` cancels stale requests when the query key changes (typing
 *     "po" then "pom" no longer races).
 *   - No manual state to clobber on phantom events.
 */
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { RiskCalculatorService } from '@client/services/riskCalculatorService';
import { formatCascadingOption } from '../utils/transformers';

const STALE_RESTRICTED = 10 * 60 * 1000; // 10 min — cascade narrows rarely change
const STALE_SHOW_ALL = 5 * 60 * 1000;   // 5  min — full list can change more often

/**
 * @param {Object} args
 * @param {('treatment_type'|'application_technique'|'products'|'units')} args.step
 * @param {string[]} [args.cultures]
 * @param {string|null} [args.treatmentType]
 * @param {string|null} [args.applicationTechnique]
 * @param {string|null} [args.productId]
 * @param {string} [args.query] - typed search text (already debounced by caller)
 * @param {boolean} [args.showAll] - false = restricted cascade, true = full list
 * @param {boolean} [args.enabled] - guard until parent declares we should fetch
 * @param {Function} [args.formatOption] - optional override of default formatter
 */
export function useCascadingOptions({
  step,
  cultures = [],
  treatmentType = null,
  applicationTechnique = null,
  productId = null,
  query = '',
  showAll = false,
  enabled = true,
  formatOption,
}) {
  // Restricted mode needs the upstream cascade to be complete before firing
  // (mirrors the original `hasDependencies` rule).
  const hasDependencies = useMemo(() => {
    if (!cultures || cultures.length === 0) return false;
    switch (step) {
      case 'treatment_type':
        return true;
      case 'application_technique':
        return !!treatmentType;
      case 'products':
      case 'units':
        return !!treatmentType && !!applicationTechnique;
      default:
        return false;
    }
  }, [step, cultures, treatmentType, applicationTechnique]);

  // Show-all does NOT require the full cascade (Mode C in the spec): the
  // user wants to bypass the narrowing, so we only need the step name.
  const queryEnabled = enabled && (showAll || hasDependencies);

  // RQ deep-compares the key; reference-unstable arrays/objects are fine.
  // Show-all only sends step/search/treatmentType, so keep the key to that:
  // otherwise every product row (different productId) refetches the same
  // full unit list.
  const queryKey = [
    'riskCalculator',
    'cascadingOptions',
    showAll
      ? { step, treatmentType, query: query || '', showAll: true }
      : {
          step,
          cultures,
          treatmentType,
          applicationTechnique,
          productId,
          query: query || '',
          showAll: false,
        },
  ];

  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1, signal }) =>
      showAll
        ? fetchShowAll({
            step,
            search: query,
            page: pageParam,
            treatmentType,
            signal,
          })
        : fetchRestricted({
            step,
            cultures,
            treatmentType,
            applicationTechnique,
            productId,
            signal,
          }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    placeholderData: keepPreviousData,
    staleTime: showAll ? STALE_SHOW_ALL : STALE_RESTRICTED,
    refetchOnWindowFocus: false,
    enabled: queryEnabled,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Format options. Default = `formatCascadingOption`; per-type override
  // (e.g. products) lets the caller customise without forking the hook.
  const formatter = useCallback(
    (opt) =>
      formatOption
        ? formatOption(opt)
        : formatCascadingOption(opt, step === 'products' ? 'product' : step),
    [formatOption, step],
  );

  const options = useMemo(() => {
    if (!result.data?.pages) return [];
    return result.data.pages.flatMap((p) => p.options || []).map(formatter);
  }, [result.data, formatter]);

  // True "no results" — query ran, completed, returned nothing.
  const hasNoResults =
    queryEnabled && !result.isFetching && options.length === 0;

  return {
    options,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: !!result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    error: result.error?.response?.data?.error || result.error?.message || null,
    refetch: result.refetch,
    hasNoResults,
    isEnabled: queryEnabled,
  };
}

// ---------------------------------------------------------------------------
// Internal fetchers
// ---------------------------------------------------------------------------

async function fetchRestricted({
  step,
  cultures,
  treatmentType,
  applicationTechnique,
  productId,
  signal,
}) {
  const data = await RiskCalculatorService.getCascadingOptions(
    {
      step,
      cultures,
      treatment_type: treatmentType,
      application_technique: applicationTechnique,
      ...(productId ? { product_id: productId } : {}),
    },
    { signal },
  );
  return {
    options: data?.options || [],
    page: 1,
    hasMore: false,
  };
}

async function fetchShowAll({ step, search, page, treatmentType, signal }) {
  const data = await RiskCalculatorService.getAllOptions(
    {
      step,
      search: search || '',
      page,
      treatmentType,
    },
    { signal },
  );
  return {
    options: data?.options || [],
    page,
    hasMore: !!data?.hasMore,
  };
}

export default useCascadingOptions;
